import React from 'react'
import {
  Card,
  CaptionText,
  HeadingText,
  BodyText,
  Button,
  Link,
  Elevation,
  Row,
  Col,
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import * as styles from './activity.css'
import { ActivityInformation, Edge } from '../../../../../typings'
import { SeeMoreButton } from './SeeMoreButton'
import { HOME_PATH, GROUP_STATUS } from '../../../constants'
import { ActivityItems } from './ActivityItems'
import {
  getGroupDetailPageVariant,
  tracking,
} from '../../../lib/analytics/fpti'
import Events from '../../../lib/analytics/eventTags.js'
import serverData from '../../../utils/server-data'

interface ActivityProps {
  groupId: string
  isAdmin?: boolean
  isPayPalUser?: boolean
  isSmallerScreen?: boolean
  activities: Edge<ActivityInformation>[]
  totalCountActivities: number
  groupStatus?: string
}

const Activity: React.FC<ActivityProps> = (props) => {
  const {
    isAdmin,
    isPayPalUser,
    isSmallerScreen,
    activities,
    totalCountActivities,
    groupId,
    groupStatus,
  } = props

  const goToLogin = () => {
    window.location.assign(`/signin/?returnUri=${HOME_PATH}/${groupId}`)
  }

  const onActivityLinkClick = () => {
    tracking(
      Events.p2p_pools_detail_recent_activity_transaction_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
      }),
    )
  }

  const isActiveGroup = groupStatus === GROUP_STATUS.ACTIVE

  const loginSection = (
    <div className={styles.loginSectionContainer} data-testid="activityLogin">
      <div className={styles.loginToViewDetailsContainer}>
        <CaptionText as="p" className={styles.loginToViewDetails}>
          <Message id="activity.loginActivityDetails" />
        </CaptionText>
      </div>
      <Button size="sm" secondary onClick={goToLogin} data-testid="loginButton">
        <Message id="activity.login" />
      </Button>
      <div className={styles.signUpContainer}>
        <CaptionText as="p" className={styles.havePaypalAccount}>
          <Message id="activity.havePaypalAccount" />
        </CaptionText>
        <Link href="/" size="sm">
          <Message id="activity.signup" />
        </Link>
      </div>
    </div>
  )

  const recentActivityTitle = () => {
    return (
      <Row data-testid="headerActivity">
        <Col xs="auto">
          <HeadingText size="sm" as="h2" className={styles.recentActivityTitle}>
            <Message id="activity.recentActivity" />
          </HeadingText>
        </Col>
      </Row>
    )
  }

  const renderTransactionsList = () => {
    return (
      <>
        <ul className={styles.noBulletStyles}>
          <ActivityItems
            isAdmin={isAdmin}
            activities={activities}
            onActivityLinkClick={onActivityLinkClick}
          />
        </ul>
        {activities?.length < totalCountActivities && (
          <SeeMoreButton groupId={groupId} activities={activities} />
        )}
      </>
    )
  }

  const renderActivities = () => {
    return (
      <Row data-testid="activityPPUserComponent">
        <Col>
          {recentActivityTitle()}
          {isSmallerScreen ? (
            <Elevation
              level={2}
              className={styles.elevationActivityStyles}
              data-testid="smallerActivity"
            >
              {renderTransactionsList()}
            </Elevation>
          ) : (
            renderTransactionsList()
          )}
        </Col>
      </Row>
    )
  }

  const renderEmptyActivities = () => {
    if (!isActiveGroup) {
      return null
    }

    return (
      <>
        {recentActivityTitle()}
        <div
          className={styles.emptyActivity}
          data-testid="activityPPUserComponent"
        >
          <BodyText>
            <Message id="activity.beTheFirst" />
          </BodyText>
        </div>
      </>
    )
  }

  const renderActivitySection = () => {
    return (
      <>
        {activities?.length > 0 ? renderActivities() : renderEmptyActivities()}
      </>
    )
  }

  const renderNonUserContent = () => {
    if (isSmallerScreen) {
      return null
    }

    return (
      <>
        {recentActivityTitle()}
        <Row data-testid="activityGuestComponent">
          <Col>
            <Card className={styles.nonUserContent} data-testid="loginDetail">
              {loginSection}
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  if (isPayPalUser) {
    return renderActivitySection()
  }

  return renderNonUserContent()
}

export default Activity
